import { AlertCircle, AlertTriangle } from "lucide-react";

import type { LicenseStatusResponse } from "@/api/types";

/**
 * Banner global de estado de licencia (Fase 10).
 *
 * - `valid`: NO muestra nada.
 * - `grace_period`: banner amarillo (warning).
 * - `expired` / `revoked` / `restricted`: banner rojo (error, modo restringido).
 * - `unknown`: NO muestra nada (DF10-08, grace implícito; el banner no
 *   debe alarmar al operador por un estado transitorio del license_client).
 */
export function LicenseBanner({
  status,
}: {
  status: LicenseStatusResponse | undefined;
}) {
  if (!status) return null;

  if (status.status === "grace_period") {
    return (
      <div
        role="alert"
        data-testid="license-banner-grace"
        className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-100 px-4 py-3 text-sm text-amber-900"
      >
        <AlertTriangle className="size-5 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p>
            <strong>Licencia en período de gracia.</strong>{" "}
            {status.expires_at
              ? `Expiró el ${formatDateTime(status.expires_at)}.`
              : "La licencia ha expirado."}{" "}
            El sistema sigue operativo hasta{" "}
            {status.grace_until ? formatDateTime(status.grace_until) : "el fin del grace"}.
          </p>
          <p className="text-xs text-amber-800">Renueva la licencia antes de esa fecha.</p>
        </div>
      </div>
    );
  }

  if (status.status === "revoked") {
    return (
      <div
        role="alert"
        data-testid="license-banner-revoked"
        className="flex items-start gap-3 rounded-md border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-900"
      >
        <AlertCircle className="size-5 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p>
            <strong>Licencia revocada.</strong> El módulo de ejecución de protocolos
            y el análisis profundo están deshabilitados.
          </p>
          <p className="text-xs text-red-800">
            Contacta al equipo AROC para resolver la situación.
          </p>
        </div>
      </div>
    );
  }

  if (status.status === "expired" || status.status === "restricted") {
    const isRestricted = status.status === "restricted";
    return (
      <div
        role="alert"
        data-testid={isRestricted ? "license-banner-restricted" : "license-banner-expired"}
        className="flex items-start gap-3 rounded-md border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-900"
      >
        <AlertCircle className="size-5 shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p>
            <strong>
              {isRestricted ? "Licencia en modo restringido." : "Licencia expirada."}
            </strong>{" "}
            El módulo de ejecución de protocolos y el análisis profundo están
            deshabilitados.
          </p>
          {status.last_validation_error && (
            <p className="text-xs text-red-800">
              Detalle: {status.last_validation_error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
